/**
 * Button Component — Soorgaa Design System
 * 
 * Architectural, high-contrast, accessible button component.
 * Complies with WCAG 2.2 AA (min 44px touch target on md, visible focus rings, aria-busy).
 * 
 * Usage:
 * <Button variant="primary" loading={isSaving} loadingText="Saving…">Save</Button>
 * <Button variant="outline" to="/student/study">Study Hub</Button>
 */
import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingText?: string;
  success?: boolean;
  successText?: string;
  error?: boolean;
  errorText?: string;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  to?: string;
  href?: string;
  /** Small up-right arrow after the label (compact action style). */
  trailingArrow?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    loadingText,
    success = false,
    successText,
    error = false,
    errorText,
    icon,
    iconPosition = "left",
    to,
    href,
    trailingArrow = false,
    className = "",
    disabled,
    children,
    ...props
  },
  ref
) {
  const baseClasses =
    "inline-flex max-w-full items-center justify-center text-center leading-snug font-semibold rounded-btn transition-all select-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none cursor-pointer";

  const sizeClasses: Record<ButtonSize, string> = {
    sm: "px-3.5 py-2 text-sm font-medium min-h-9 gap-1.5",
    md: "px-4 py-2 text-sm min-h-[44px] gap-2",
    lg: "px-6 py-3 text-base min-h-[48px] gap-2.5",
  };

  const variantClasses: Record<ButtonVariant, string> = {
    primary:
      "bg-primary text-on-accent border border-primary/90 hover:opacity-90 active:translate-y-[1px] shadow-xs",
    secondary:
      "bg-secondary text-secondary-foreground border border-border hover:bg-muted active:translate-y-[1px]",
    outline:
      "bg-transparent text-foreground border border-border hover:bg-muted active:translate-y-[1px]",
    ghost:
      "bg-transparent text-foreground hover:bg-muted/80 active:translate-y-[1px]",
    destructive:
      "bg-destructive text-destructive-foreground border border-destructive/90 hover:opacity-90 active:translate-y-[1px] shadow-xs",
  };

  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`.trim();

  const content = (
    <>
      {loading ? (
        <>
          <Loader2 size={size === "sm" ? 14 : 16} className="animate-spin shrink-0" aria-hidden="true" />
          <span className="min-w-0 break-words">{loadingText || "Processing…"}</span>
        </>
      ) : (
        <>
          {icon && iconPosition === "left" && <span className="shrink-0">{icon}</span>}
          {success ? <span className="min-w-0 break-words">{successText || children}</span> : error ? <span className="min-w-0 break-words">{errorText || children}</span> : children && <span className="min-w-0 break-words">{children}</span>}
          {icon && iconPosition === "right" && <span className="shrink-0">{icon}</span>}
          {trailingArrow && <ArrowUpRight size={14} className="shrink-0" aria-hidden="true" />}
        </>
      )}
    </>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className={combinedClasses} aria-busy={loading}>
        {content}
      </Link>
    );
  }

  if (href && !disabled) {
    return (
      <a href={href} className={combinedClasses} aria-busy={loading}>
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={props.type || "button"}
      disabled={disabled || loading}
      aria-busy={loading}
      className={combinedClasses}
      {...props}
    >
      {content}
    </button>
  );
});
